// src/features/mocktests/components/WritingPage.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Paper,
  Backdrop,
  Card,
  Fade,
} from "@mui/material";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import {
  useLazyGetPracticeTestDataQuery,
  useLazyGetTestAttemptResultsQuery,
  useUpdateTestAttemptMutation,
  useLazyGetInstructionsQuery,
  useAcknowledgeInstructionMutation,
} from "../mockTestSlice";
import WritingSection from "./WritingSection";
import TestResultsPopup from "./TestResultsPopup";
import { getStoredTimerData, storeTimerData } from "../utils/practiceTimer";
import {
  InstructionWrapper,
  useInstructions,
} from "../../../components/Instructions";

export default function WritingPage() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // --- Instruction system integration ---
  const {
    instructionState,
    showInstructionModal,
    hideInstructionModal,
    hideConfirmationDialog,
    hideUnattemptedDialog,
    hideTimeExpiryDialog,
  } = useInstructions();

  // Check for essential state and redirect if missing (browser refresh)
  const studentAttemptId =
    location.state?.studentAttemptId ||
    location.state?.mockTest?.studentAttemptId;

  // Instruction API hooks (must be before useEffect)
  const [
    getInstructions,
    { data: instructionData, isLoading: instructionLoading },
  ] = useLazyGetInstructionsQuery();
  const [acknowledgeInstruction] = useAcknowledgeInstructionMutation();

  // Show Writing instructions at section start
  const [showInitialInstructions, setShowInitialInstructions] = useState(true);
  const [instructionsAcknowledged, setInstructionsAcknowledged] =
    useState(false);

  // Fetch instructions when component mounts
  useEffect(() => {
    if (showInitialInstructions && studentAttemptId) {
      getInstructions({
        studentAttemptId,
        module: "writing",
      });
      const mode = location.state?.isPracticeMode ? "practice" : "simulation";
      showInstructionModal("writing", null, mode);
      setShowInitialInstructions(false);
    }
  }, [
    showInitialInstructions,
    studentAttemptId,
    getInstructions,
    showInstructionModal,
    location.state?.isPracticeMode,
  ]);

  useEffect(() => {
    console.log("WritingPage mount check:", {
      studentAttemptId,
      hasState: !!location.state,
    });
    if (!studentAttemptId || !location.state) {
      console.log(
        "WritingPage: Missing required state, redirecting to mock test main page"
      );
      navigate(`/mock-test/${testId}`, { replace: true });
    }
  }, [studentAttemptId, location.state, navigate, testId]);

  // Practice mode timer inputs
  const timerMinutes = location.state?.timerMinutes;
  const testAttemptId = location.state?.mockTest?.studentAttemptId;

  // Get any stored timer for this attempt
  const storedTimer = getStoredTimerData(testAttemptId);

  // Timer state (we will start it only after user chooses submission type)
  const [practiceTimerStartTime, setPracticeTimerStartTime] = useState(null);
  const [practiceTimerEndTime, setPracticeTimerEndTime] = useState(null);
  const [adjustedEndTime, setAdjustedEndTime] = useState(null); // For handling preparation time skips
  const [hasChosenSubmission, setHasChosenSubmission] = useState(true); // UPDATED: Set to true since we're bypassing the submission type dialog

  // Restore in-progress timer if present (e.g., refresh after selection)
  useEffect(() => {
    if (storedTimer?.startTime && storedTimer?.endTime) {
      setPracticeTimerStartTime(storedTimer.startTime);
      setPracticeTimerEndTime(storedTimer.endTime);
      setHasChosenSubmission(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update API mutation
  const [updateTestAttempt] = useUpdateTestAttemptMutation();

  // Fetch test data (PRACTICE ONLY) - moved up for timer data access
  const [
    triggerGetPracticeData,
    {
      data: practiceTestData,
      isLoading: practiceLoading,
      error: practiceError,
    },
  ] = useLazyGetPracticeTestDataQuery();
  const [localWritingData, setLocalWritingData] = useState(null);

  // Extract timer data from API response (moved up before useEffect)
  const apiStartTime =
    practiceTestData?.data?.startTime || // From practice test data API response
    localWritingData?.data?.startTime || // From stored local data
    location.state?.mockTest?.startTime ||
    location.state?.mockTest?.testStartTime;
  const apiEndTime =
    practiceTestData?.data?.endTime || // From practice test data API response
    localWritingData?.data?.endTime || // From stored local data
    location.state?.mockTest?.endTime ||
    location.state?.mockTest?.testEndDate;

  // Once the user chooses Computer/Paper, start a fresh timer based on API duration (or fallback)
  useEffect(() => {
    if (!hasChosenSubmission) return;
    if (practiceTimerStartTime && practiceTimerEndTime) return; // already restored

    // Compute duration: prefer API start/end → else timerMinutes → else 45m
    let durationMs = 0;
    if (apiStartTime && apiEndTime) {
      durationMs =
        new Date(apiEndTime).getTime() - new Date(apiStartTime).getTime();
    } else if (timerMinutes) {
      durationMs = timerMinutes * 60 * 1000;
    } else {
      durationMs = 45 * 60 * 1000;
    }

    const now = Date.now();
    const end = now + durationMs;

    const startISO = new Date(now).toISOString();
    const endISO = new Date(end).toISOString();

    setPracticeTimerStartTime(startISO);
    setPracticeTimerEndTime(endISO);
    storeTimerData(startISO, endISO, testAttemptId);

    // ✅ Persist the timer window to backend
    (async () => {
      try {
        await updateTestAttempt({
          studentAttemptId: testAttemptId,
          testStartTime: startISO,
          testEndDate: endISO,
        }).unwrap();
      } catch (e) {
        console.error("Failed to update attempt timings", e);
      }
    })();
  }, [
    hasChosenSubmission,
    practiceTimerStartTime,
    practiceTimerEndTime,
    apiStartTime,
    apiEndTime,
    timerMinutes,
    testAttemptId,
    updateTestAttempt,
  ]);

  // Dialog and user flow states
  const [evaluationDialogOpen, setEvaluationDialogOpen] = useState(false);
  const [showEvalConfirmation, setShowEvalConfirmation] = useState(false);
  // COMMENTED: Paper-based modal - directly show computer-based
  const [showSubmissionTypeDialog, setShowSubmissionTypeDialog] =
    useState(false); // Changed from true to false

  const [evaluationType] = useState("human"); // lock to human flow
  const [selectedEvalType, setSelectedEvalType] = useState(null);
  const [submissionType, setSubmissionType] = useState("computer"); // "computer" | "paper"

  // State for WritingSection
  const [answer, setAnswer] = useState("");

  // Task navigation functions
  const handleTaskChange = (newTaskIndex) => {
    // Save current answer before switching
    setTaskAnswers((prev) => ({
      ...prev,
      [currentTaskIndex]: answer,
    }));

    // Switch to new task
    setCurrentTaskIndex(newTaskIndex);

    // Load answer for new task
    setAnswer(taskAnswers[newTaskIndex] || "");
  };

  const handleAnswerChange = (newAnswer) => {
    setAnswer(newAnswer);
    // Also update the taskAnswers state for current task
    setTaskAnswers((prev) => ({
      ...prev,
      [currentTaskIndex]: newAnswer,
    }));
  };

  // Footer confirm dialog
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Results
  const [testResults, setTestResults] = useState(null);
  const [showResultsPopup, setShowResultsPopup] = useState(false);

  // Timer tick state
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [timerEnded, setTimerEnded] = useState(false);

  // Header visibility state
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);

  // Ref to access WritingSection methods
  const writingSectionRef = useRef();

  // API hooks
  const [getTestAttemptResults] = useLazyGetTestAttemptResultsQuery();

  // Helper: show results (practice mode)
  const showPracticeResults = async (studentAttemptIdParam) => {
    try {
      console.log("showPracticeResults: fetching results for", studentAttemptIdParam);
      const resultsResponse = await getTestAttemptResults(
        studentAttemptIdParam
      ).unwrap();
      // Ensure any overlay dialogs are closed so popup can be seen
      setEvaluationDialogOpen(false);
      setConfirmOpen(false);
      setTestResults(resultsResponse);
      setShowResultsPopup(true);
    } catch (resultsError) {
      console.error("showPracticeResults error:", resultsError);
      alert("Error fetching results. Please try again.");
    }
  };

  // Should we show results popup after writing?
  const shouldShowResultsPopup = () => {
    const isPracticeMode = location.state?.isPracticeMode;
    const selectedSections = location.state?.selectedSections;

    if (!isPracticeMode) return true;

    if (!selectedSections?.writing) return false;

    const hasListening = selectedSections.listening;
    const hasReading = selectedSections.reading;
    return hasListening || hasReading;
  };

  // Show Writing instructions when data is loaded
  useEffect(() => {
    if (showInitialInstructions && localWritingData) {
      const mode = location.state?.isPracticeMode ? "practice" : "simulation";
      showInstructionModal("writing", null, mode);
      setShowInitialInstructions(false);
    }
  }, [
    showInitialInstructions,
    localWritingData,
    showInstructionModal,
    location.state,
  ]);

  // Debug location state
  useEffect(() => {
    // console.log removed
  }, [location.state]);

  // Writing task navigation state
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [writingTasks, setWritingTasks] = useState([]);
  const [taskAnswers, setTaskAnswers] = useState({}); // Store answers for each task

  useEffect(() => {
    const practiceDataFromState = location.state?.practiceData;
    console.log(
      "🔍 WritingPage: Practice data from location state:",
      practiceDataFromState
    );

    // Allow setting data from navigation state even before acknowledgment
    if (practiceDataFromState) {
      setLocalWritingData(practiceDataFromState);
      // Extract writing tasks from practice data - check multiple possible data structures
      const tasks =
        practiceDataFromState?.tasks ||
        practiceDataFromState?.data?.writingTest?.writingTasks ||
        practiceDataFromState?.data?.writingTest?.tasks ||
        practiceDataFromState?.data?.tasks ||
        [];
      console.log("📝 WritingPage: Writing tasks from state:", tasks);
      console.log("📝 WritingPage: Number of tasks from state:", tasks.length);
      console.log(
        "📝 WritingPage: Full practiceDataFromState structure:",
        JSON.stringify(practiceDataFromState, null, 2)
      );
      setWritingTasks(tasks);
      // Initialize task answers
      const answers = {};
      tasks.forEach((task, index) => {
        answers[index] = "";
      });
      setTaskAnswers(answers);
      console.log("📝 WritingPage: Initialized answers from state:", answers);
      return;
    }

    // Don't fetch data from API until instructions are acknowledged
    if (!instructionsAcknowledged) {
      return;
    }

    setLocalWritingData(null);

    const studentAttemptIdLocal = location.state?.studentAttemptId;
    console.log("=== WRITING PAGE DATA FETCH DEBUG ===");
    console.log("studentAttemptIdLocal:", studentAttemptIdLocal);
    console.log("location.state:", location.state);
    console.log("isPracticeMode:", location.state?.isPracticeMode);
    console.log("simulationStep:", location.state?.simulationStep);
    console.log("writingTestId:", location.state?.writingTestId);
    console.log("writingTaskIds from state:", location.state?.writingTaskIds);
    console.log(
      "writingTaskIds from ieltsTest:",
      location.state?.mockTest?.ieltsTest?.writingTaskIds
    );
    console.log(
      "writingTaskIds from oetTest:",
      location.state?.mockTest?.oetTest?.writingTaskIds
    );
    console.log("=== END DEBUG ===");

    if (studentAttemptIdLocal) {
      // Get writing task IDs if available and add them all
      const writingTaskIds =
        location.state?.writingTaskIds ||
        location.state?.simulationStep?.writingTaskIds ||
        location.state?.mockTest?.ieltsTest?.writingTaskIds ||
        location.state?.mockTest?.oetTest?.writingTaskIds ||
        [];

      // Also get writing ID from simulation step
      const writingId =
        location.state?.writingTestId ||
        location.state?.simulationStep?.writingId ||
        location.state?.mockTest?.ieltsTest?.writingId ||
        location.state?.mockTest?.oetTest?.writingId;

      console.log("Final writingTaskIds:", writingTaskIds);
      console.log("Final writingId:", writingId);

      const apiParams = {
        studentAttemptId: studentAttemptIdLocal,
        currentSection: "writing",
        writingId: writingId,
      };

      if (writingTaskIds.length > 0) {
        // Send all writing task IDs to get data for both tasks
        apiParams.writingTaskIds = writingTaskIds;
        console.log("API params for writing (both tasks):", apiParams);
      }

      triggerGetPracticeData(apiParams);
    }
    // eslint-disable-next-line
  }, [
    location.state,
    location.state?.studentAttemptId,
    location.state?.practiceData,
    location.state?.writingTestId,
    location.state?.writingTaskIds,
    instructionsAcknowledged,
    triggerGetPracticeData,
  ]);

  // Extract submission flags from API data
  const isWritingSubmitted = localWritingData?.data?.isWritingSubmitted || 
    practiceTestData?.data?.isWritingSubmitted || 
    location.state?.practiceData?.data?.isWritingSubmitted || 
    false;
  
  const isIeltsWritingSubmited = localWritingData?.data?.isIeltsWritingSubmited || 
    practiceTestData?.data?.isIeltsWritingSubmited || 
    location.state?.practiceData?.data?.isIeltsWritingSubmited || 
    { isTask1Submitted: false, isTask2Submitted: false };

  useEffect(() => {
    if (practiceTestData) {
      console.log(
        "🔍 WritingPage: Practice test data received:",
        practiceTestData
      );
      setLocalWritingData(practiceTestData);
      // Extract writing tasks from API response - check multiple possible data structures
      const tasks =
        practiceTestData?.tasks ||
        practiceTestData?.data?.writingTest?.writingTasks ||
        practiceTestData?.data?.writingTest?.tasks ||
        practiceTestData?.data?.tasks ||
        [];
      console.log("📝 WritingPage: Extracted writing tasks:", tasks);
      console.log("📝 WritingPage: Number of writing tasks:", tasks.length);
      console.log(
        "📝 WritingPage: Full practiceTestData structure:",
        JSON.stringify(practiceTestData, null, 2)
      );
      console.log("📝 WritingPage: isWritingSubmitted:", practiceTestData?.data?.isWritingSubmitted);
      console.log("📝 WritingPage: isIeltsWritingSubmited:", practiceTestData?.data?.isIeltsWritingSubmited);
      setWritingTasks(tasks);
      // Initialize task answers if not already set
      if (Object.keys(taskAnswers).length === 0 && tasks.length > 0) {
        const answers = {};
        tasks.forEach((task, index) => {
          answers[index] = "";
        });
        setTaskAnswers(answers);
        console.log("📝 WritingPage: Initialized task answers:", answers);
      }
    }
  }, [practiceTestData, taskAnswers]);

  // Handle adjustment of end time from WritingSection (e.g., when preparation time is skipped)
  const handleEndTimeAdjustment = useCallback((newEndTime) => {
    // console.log removed
    setAdjustedEndTime(newEndTime);
  }, []);

  const handleResultsReceived = (results) => {
    if (shouldShowResultsPopup()) {
      setTestResults(results);
      setShowResultsPopup(true);
    }
  };

  const handleTimeUp = useCallback(() => {
    // console.log removed
  }, []);

  // Tick the countdown only after user has chosen and we have an endTime
  useEffect(() => {
    if (!hasChosenSubmission || !practiceTimerEndTime) return;

    const updateTimer = () => {
      const now = Date.now();
      // Use adjusted end time if available, otherwise use original end time
      const effectiveEndTime = adjustedEndTime || practiceTimerEndTime;
      const endMs = new Date(effectiveEndTime).getTime();
      const remainingSeconds = Math.max(0, Math.floor((endMs - now) / 1000));
      setTimeRemaining(remainingSeconds);

      if (remainingSeconds <= 0 && !timerEnded) {
        setTimerEnded(true);
        handleTimeUp();
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [
    hasChosenSubmission,
    practiceTimerEndTime,
    adjustedEndTime,
    handleTimeUp,
    timerEnded,
  ]);

  // Transform API data for WritingSection
  const transformWritingData = (apiData) => {
    const writingTest = apiData?.data?.writingTest;
    if (!writingTest) return null;
    return {
      title: writingTest.title || "",
      subTitle: writingTest.subTitle || "",
      instructions: writingTest.instructions || "",
      writingPrompt: writingTest.writingPrompt || "",
      caseNotes: (writingTest.writingSection || []).map((section) => ({
        title: section.title,
        instructions: section.instructions,
        subSections: (section.subSections || []).map((sub) => ({
          label: sub.label,
          content: sub.content,
          visible: sub.visible,
          _id: sub._id,
        })),
        _id: section._id,
      })),
    };
  };
  const dataToTransform = localWritingData || location.state?.practiceData;
  const writingData = transformWritingData(dataToTransform);

  const handleSubmissionTypeSelect = (type) => {
    setSubmissionType(type);
    setEvaluationDialogOpen(false);
    setShowSubmissionTypeDialog(false);
    setHasChosenSubmission(true); // <-- start the timer flow
  };

  // Footer: confirm submit
  const handleConfirmSubmit = () => {
    setConfirmOpen(false);
    if (writingSectionRef.current?.submitAnswer) {
      writingSectionRef.current.submitAnswer();
    }
  };

  // Time formatting helpers
  const pad2 = (n) => String(n).padStart(2, "0");
  const fmtClock = (s) => {
    if (s == null) return "--:--:--";
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = Math.floor(s % 60);
    return `${pad2(h)}:${pad2(m)}:${pad2(sec)}`;
  };

  const getDisplayTime = () => {
    if (!hasChosenSubmission || !practiceTimerEndTime) return fmtClock(null);
    return fmtClock(timeRemaining);
  };

  if (
    practiceLoading ||
    (!localWritingData &&
      !location.state?.practiceData &&
      instructionsAcknowledged)
  ) {
    return (
      <>
        {/* Instruction System Wrapper - must render even during loading */}
        <InstructionWrapper
          instructionState={instructionState}
          onInstructionNext={async () => {
            // Acknowledge instruction when user clicks Next
            if (studentAttemptId) {
              try {
                await acknowledgeInstruction({
                  studentAttemptId,
                  module: "writing",
                }).unwrap();
                setInstructionsAcknowledged(true);
              } catch (error) {
                console.error("Failed to acknowledge instruction:", error);
              }
            }
            hideInstructionModal();
          }}
          onInstructionClose={async () => {
            // Acknowledge instruction when modal closes
            if (studentAttemptId) {
              try {
                await acknowledgeInstruction({
                  studentAttemptId,
                  module: "writing",
                }).unwrap();
                setInstructionsAcknowledged(true);
              } catch (error) {
                console.error("Failed to acknowledge instruction:", error);
              }
            }
            hideInstructionModal();
          }}
          onConfirmationConfirm={() => {
            hideConfirmationDialog();
            hideUnattemptedDialog();
          }}
          onConfirmationClose={hideConfirmationDialog}
          onUnattemptedContinue={() => {
            hideUnattemptedDialog();
          }}
          onUnattemptedClose={() => {
            hideUnattemptedDialog();
          }}
          onTimeExpiryContinue={hideTimeExpiryDialog}
          timeRemaining={null}
          unattemptedCount={0}
          nextSection="speaking"
          instructionData={instructionData?.data}
          instructionLoading={instructionLoading}
        />
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
        >
          <CircularProgress size={60} thickness={5} />
          <Typography sx={{ ml: 2 }}>Loading writing data...</Typography>
        </Box>
      </>
    );
  }

  if (practiceError) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <Typography color="error" variant="h6" gutterBottom>
          Error loading writing test:{" "}
          {practiceError.message || JSON.stringify(practiceError)}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Instruction System Wrapper */}
      <InstructionWrapper
        instructionState={instructionState}
        onInstructionNext={async () => {
          // Acknowledge instruction when user clicks Next
          if (studentAttemptId) {
            try {
              await acknowledgeInstruction({
                studentAttemptId,
                module: "writing",
              }).unwrap();
              setInstructionsAcknowledged(true);
            } catch (error) {
              console.error("Failed to acknowledge instruction:", error);
            }
          }
          hideInstructionModal();
        }}
        onInstructionClose={async () => {
          // Acknowledge instruction when modal closes
          if (studentAttemptId) {
            try {
              await acknowledgeInstruction({
                studentAttemptId,
                module: "writing",
              }).unwrap();
              setInstructionsAcknowledged(true);
            } catch (error) {
              console.error("Failed to acknowledge instruction:", error);
            }
          }
          hideInstructionModal();
        }}
        onConfirmationConfirm={() => {
          hideConfirmationDialog();
          hideUnattemptedDialog();
        }}
        onConfirmationClose={hideConfirmationDialog}
        onUnattemptedContinue={() => {
          hideUnattemptedDialog();
        }}
        onUnattemptedClose={() => {
          hideUnattemptedDialog();
        }}
        onTimeExpiryContinue={hideTimeExpiryDialog}
        timeRemaining={null}
        unattemptedCount={0}
        nextSection="speaking"
        instructionData={instructionData?.data}
        instructionLoading={instructionLoading}
      />

      <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
        {/* HEADER TOGGLE BUTTON - Fixed position when header is hidden */}
        {!isHeaderVisible && (
          <Fade in={!isHeaderVisible} timeout={300}>
            <Box
              sx={{
                position: "fixed",
                top: 10,
                left: 0,
                right: 0,
                zIndex: 30,
                display: "flex",
                justifyContent: "center",
                pt: 1,
                pb: 1,
                bgcolor: "rgba(247, 247, 247, 0.95)",
                backdropFilter: "blur(10px)",
                borderBottom: "1px solid rgba(157, 34, 53, 0.1)",
              }}
            >
              <Button
                onClick={() => setIsHeaderVisible(true)}
                variant="contained"
                size="small"
                sx={{
                  background:
                    "linear-gradient(135deg, #9D2235 0%, #7a182a 100%)",
                  color: "white",
                  px: 2.5,
                  py: 0.75,
                  fontSize: "0.8rem",
                  fontWeight: 500,
                  boxShadow: "0px 4px 16px rgba(157, 34, 53, 0.3)",
                  border: "none",
                  borderRadius: 25,
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #7a182a 0%, #5d1220 100%)",
                    transform: "translateY(-1px)",
                    boxShadow: "0px 6px 20px rgba(157, 34, 53, 0.4)",
                  },
                  "&:active": {
                    transform: "translateY(0px)",
                  },
                  textTransform: "none",
                }}
              >
                ▼ Show Header
              </Button>
            </Box>
          </Fade>
        )}

        {/* CONTENT AREA */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            overflow: "hidden",
            flexDirection: "column",
            pt: !isHeaderVisible ? { xs: "70px", sm: "75px" } : 0,
            transition: "padding-top 0.3s ease-in-out",
          }}
        >
          {/* Only show WritingSection when all modals are closed */}
          {!evaluationDialogOpen &&
            !showEvalConfirmation
            // COMMENTED: && !showSubmissionTypeDialog // Paper-based modal removed
            && (
              <WritingSection
                ref={writingSectionRef}
                writingData={writingData}
                answer={answer}
                onAnswerChange={handleAnswerChange}
                // Writing task navigation props
                writingTasks={writingTasks}
                currentTaskIndex={currentTaskIndex}
                onTaskChange={handleTaskChange}
                taskAnswers={taskAnswers}
                onTimeUp={handleTimeUp}
                timer="no-limit"
                mode={
                  location.state?.isPracticeMode ? "practice" : "simulation"
                }
                submissionType={submissionType}
                studentAttemptId={location.state?.studentAttemptId || ""}
                writingTestId={
                  location.state?.writingId ||
                  location.state?.writingTestId ||
                  ""
                }
                isAiBased={evaluationType === "ai"}
                onResultsReceived={
                  shouldShowResultsPopup() ? handleResultsReceived : null
                }
                shouldFetchResults={shouldShowResultsPopup()}
                // Submission status flags
                isWritingSubmitted={isWritingSubmitted}
                isIeltsWritingSubmited={isIeltsWritingSubmited}
                // Use our post-selection timer
                // Use API timer data if available, otherwise use our post-selection timer
                startTime={apiStartTime || practiceTimerStartTime}
                endTime={apiEndTime || adjustedEndTime || practiceTimerEndTime}
                onEndTimeAdjustment={handleEndTimeAdjustment} // ✅ pass the callback for time adjustments
                updateTestAttempt={updateTestAttempt} // ✅ pass the mutation function
                testId={testId}
                isHeaderVisible={isHeaderVisible} // ✅ pass header visibility state
                instrOpen={
                  instructionState.showInstructions &&
                  instructionState.currentSection === "writing"
                }
                // Footer-related props
                confirmOpen={confirmOpen}
                setConfirmOpen={setConfirmOpen}
                onConfirmSubmit={handleConfirmSubmit}
                onBack={() => {
                  const sectionOrder = location.state?.sectionOrder || [];
                  const currentSectionIndex =
                    location.state?.currentSectionIndex || 0;

                  if (currentSectionIndex > 0) {
                    const previousSection =
                      sectionOrder[currentSectionIndex - 1];
                    navigate(`/mock-test/${testId}/${previousSection}`, {
                      state: {
                        ...location.state,
                        currentSectionIndex: currentSectionIndex - 1,
                      },
                    });
                  }
                }}
                isLastQuestion={
                  location.state?.isPracticeMode &&
                  location.state?.sectionOrder &&
                  location.state?.currentSectionIndex ===
                    location.state?.sectionOrder.length - 1
                }
                onFinish={async () => {
                  if (location.state?.isPracticeMode) {
                    const sid = location.state?.studentAttemptId;
                    try {
                      await showPracticeResults(sid);
                    } catch (error) {
                      navigate("/mock-tests");
                    }
                  } else {
                    // Simulation mode - check if there are more sections or finish test
                    console.log("Writing finished in simulation mode");
                    const simulationStep = location.state?.simulationStep;

                    // Check if there's a speaking section
                    const hasSpeaking =
                      location.state?.mockTest?.ieltsTest?.speakingId ||
                      location.state?.mockTest?.oetTest?.speakingId;

                    if (hasSpeaking) {
                      console.log("Navigating to speaking section");
                      const updatedSimulationStep = {
                        ...simulationStep,
                        module: "speaking",
                        writingCompleted: true,
                        currentSection: "speaking",
                        previousSection: "writing",
                      };

                      navigate(`/speaking`, {
                        state: {
                          ...location.state,
                          simulationStep: updatedSimulationStep,
                        },
                      });
                    } else {
                      console.log("No speaking section, navigating to results");
                      // Navigate to results or dashboard
                      navigate(`/mock-test/${testId}/results`, {
                        state: {
                          ...location.state,
                          simulationStep: {
                            ...simulationStep,
                            writingCompleted: true,
                            testCompleted: true,
                          },
                        },
                      });
                    }
                  }
                }}
              />
            )}
        </Box>

        {/* Footer */}
        {!evaluationDialogOpen &&
          !showEvalConfirmation
          // COMMENTED: && !showSubmissionTypeDialog // Paper-based modal removed
          && (
            <Box
              sx={{
                bgcolor: "#fff",
                borderTop: "1px solid #e0e0e0",
                px: { xs: 1, sm: 4 },
                py: { xs: 1.5, sm: 2 },
                position: "sticky",
                bottom: 0,
                zIndex: 10,
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {location.state?.sectionOrder &&
                location.state?.currentSectionIndex > 0 && (
                  <Button
                    variant="outlined"
                    onClick={() => {
                      const sectionOrder = location.state?.sectionOrder || [];
                      const currentSectionIndex =
                        location.state?.currentSectionIndex || 0;

                      if (currentSectionIndex > 0) {
                        const previousSection =
                          sectionOrder[currentSectionIndex - 1];
                        navigate(`/mock-test/${testId}/${previousSection}`, {
                          state: {
                            ...location.state,
                            currentSectionIndex: currentSectionIndex - 1,
                          },
                        });
                      }
                    }}
                    sx={{
                      minWidth: { xs: 80, sm: 100 },
                      textTransform: "none",
                      borderColor: "#9D2235",
                      color: "#9D2235",
                      py: { xs: 0.75, sm: 1 },
                      px: { xs: 1.5, sm: 2 },
                      borderRadius: 3,
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                      "&:hover": {
                        borderColor: "#9D2235",
                        backgroundColor: "rgba(157, 34, 53, 0.04)",
                      },
                    }}
                  >
                    Back
                  </Button>
                )}
              {!(
                location.state?.sectionOrder &&
                location.state?.currentSectionIndex > 0
              ) && <Box />}
              {/* 
                Determine button states based on submission status:
                - Allow navigation between tasks always
                - Disable Submit button when task is already submitted
                - In simulation mode, show Finish button to proceed to results
              */}
              {(() => {
                const isPracticeMode = location.state?.isPracticeMode;
                const selectedSections = location.state?.selectedSections;
                
                // Check if listening or reading is also selected in practice mode
                const hasListeningOrReading = isPracticeMode && 
                  (selectedSections?.listening || selectedSections?.reading);
                
                // Check if all tasks are submitted (for simulation mode)
                const allTasksSubmitted = 
                  (isIeltsWritingSubmited?.isTask1Submitted || false) && 
                  (isIeltsWritingSubmited?.isTask2Submitted || false);
                
                // Check if writing is fully submitted (either flag or all individual tasks)
                const isWritingFullySubmitted = isWritingSubmitted || allTasksSubmitted;
                
                // Check if current task is submitted
                const isCurrentTaskAlreadySubmitted = (() => {
                  // In simulation mode, check overall writing submission or all tasks
                  if (!isPracticeMode && isWritingFullySubmitted) {
                    return true;
                  }
                  // In practice mode, check individual task submission
                  if (currentTaskIndex === 0) {
                    return isIeltsWritingSubmited?.isTask1Submitted || false;
                  } else if (currentTaskIndex === 1) {
                    return isIeltsWritingSubmited?.isTask2Submitted || false;
                  }
                  return false;
                })();

                // In practice mode with listening/reading selected AND all writing tasks submitted
                // Show Finish button to view listening/reading results
                if (isPracticeMode && hasListeningOrReading && isWritingFullySubmitted) {
                  return (
                    <Button
                      variant="contained"
                      size="large"
                      onClick={async () => {
                        // Use shared helper to fetch and show practice results
                        console.log("Practice Finish clicked - fetching results for:", studentAttemptId);
                        try {
                          await showPracticeResults(studentAttemptId);
                        } catch (error) {
                          console.error("Error in practice finish:", error);
                          // fallback: navigate back to tests
                          navigate("/app/mocktests");
                        }
                      }}
                      sx={{
                        minWidth: { xs: 120, sm: 140 },
                        textTransform: "none",
                        bgcolor: "#9D2235",
                        py: { xs: 0.75, sm: 1 },
                        px: { xs: 2, sm: 3 },
                        borderRadius: 3,
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                        "&:hover": { bgcolor: "#7a182a" },
                      }}
                    >
                      Finish
                    </Button>
                  );
                }

                // In simulation mode with writing already submitted, show Finish button to proceed
                if (!isPracticeMode && isWritingFullySubmitted) {
                  return (
                    <Button
                      variant="contained"
                      size="large"
                      onClick={async () => {
                        // Fetch results and show the popup
                        try {
                          const resultsResponse = await getTestAttemptResults(
                            studentAttemptId
                          ).unwrap();
                          setTestResults(resultsResponse);
                          setShowResultsPopup(true);
                        } catch (error) {
                          console.error("Error fetching results:", error);
                          // If fetching fails, still navigate to results page
                          const simulationStep = location.state?.simulationStep;
                          const hasSpeaking =
                            location.state?.mockTest?.ieltsTest?.speakingId ||
                            location.state?.mockTest?.oetTest?.speakingId;

                          if (hasSpeaking) {
                            const updatedSimulationStep = {
                              ...simulationStep,
                              module: "speaking",
                              writingCompleted: true,
                              currentSection: "speaking",
                              previousSection: "writing",
                            };
                            navigate(`/speaking`, {
                              state: {
                                ...location.state,
                                simulationStep: updatedSimulationStep,
                              },
                            });
                          } else {
                            navigate(`/mock-test/${testId}/results`, {
                              state: {
                                ...location.state,
                                simulationStep: {
                                  ...simulationStep,
                                  writingCompleted: true,
                                  testCompleted: true,
                                },
                              },
                            });
                          }
                        }
                      }}
                      sx={{
                        minWidth: { xs: 120, sm: 140 },
                        textTransform: "none",
                        bgcolor: "#9D2235",
                        py: { xs: 0.75, sm: 1 },
                        px: { xs: 2, sm: 3 },
                        borderRadius: 3,
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                        "&:hover": { bgcolor: "#7a182a" },
                      }}
                    >
                      Finish
                    </Button>
                  );
                }

                // Show Next button if there are multiple tasks and not on last task
                if (writingTasks.length > 1 && currentTaskIndex < writingTasks.length - 1) {
                  return (
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => handleTaskChange(currentTaskIndex + 1)}
                      sx={{
                        minWidth: { xs: 120, sm: 140 },
                        textTransform: "none",
                        bgcolor: "#9D2235",
                        py: { xs: 0.75, sm: 1 },
                        px: { xs: 2, sm: 3 },
                        borderRadius: 3,
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                      }}
                    >
                      Next Task
                    </Button>
                  );
                }

                // On last task - show Submit/Finish button (disabled if already submitted)
                return (
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => setConfirmOpen(true)}
                    disabled={isCurrentTaskAlreadySubmitted}
                    sx={{
                      minWidth: { xs: 120, sm: 140 },
                      textTransform: "none",
                      bgcolor: isCurrentTaskAlreadySubmitted ? "#cccccc" : "#9D2235",
                      py: { xs: 0.75, sm: 1 },
                      px: { xs: 2, sm: 3 },
                      borderRadius: 3,
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                      "&.Mui-disabled": {
                        bgcolor: "#cccccc",
                        color: "rgba(0, 0, 0, 0.5)",
                      },
                    }}
                  >
                    {isCurrentTaskAlreadySubmitted
                      ? "Already Submitted"
                      : location.state?.isPracticeMode &&
                        location.state?.sectionOrder &&
                        location.state?.currentSectionIndex ===
                          location.state?.sectionOrder.length - 1
                      ? "Finish"
                      : "Submit"}
                  </Button>
                );
              })()}
            </Box>
          )}
      </Box>

      {/* Backdrop for modal blur */}
      <Backdrop
        open={
          evaluationDialogOpen ||
          showEvalConfirmation
          // COMMENTED: || showSubmissionTypeDialog // Paper-based modal removed
        }
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backdropFilter: "blur(5px)",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        }}
      />

      {/* (Disabled) Evaluation Method Dialog - kept but never opened */}
      <Dialog
        open={
          evaluationDialogOpen &&
          !showEvalConfirmation
          // COMMENTED: && !showSubmissionTypeDialog // Paper-based modal removed
        }
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 700,
            color: "#9D2235",
            fontSize: 28,
            pt: 4,
          }}
        >
          Choose Evaluation Method
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="subtitle1"
            sx={{ textAlign: "center", mb: 4, fontSize: 18, color: "#555" }}
          >
            Select how you would like your writing to be evaluated
          </Typography>
          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} sm={6}>
              <Paper
                elevation={3}
                onClick={() => setSelectedEvalType("human")}
                sx={{
                  p: 4,
                  textAlign: "center",
                  borderRadius: 3,
                  cursor: "pointer",
                  transition: "all 0.3s",
                  border: "2px solid transparent",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: 6,
                    border: "2px solid #9D2235",
                  },
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  background:
                    "linear-gradient(135deg, #ffffff 0%, #f8f8f8 100%)",
                }}
              >
                <Box
                  sx={{
                    bgcolor: "#FFE0E4",
                    borderRadius: "50%",
                    p: 2,
                    mb: 3,
                    width: 100,
                    height: 100,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <PersonOutlineOutlinedIcon
                    sx={{ fontSize: 60, color: "#9D2235" }}
                  />
                </Box>
                <Typography variant="h5" fontWeight={600} sx={{ mb: 1 }}>
                  Human-Based Evaluation
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Get detailed feedback from our expert human evaluators
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      {/* (Disabled) Confirm Selection Dialog - kept but never opened */}
      <Dialog
        open={showEvalConfirmation}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 2 } }}
      >
        <DialogTitle
          sx={{ textAlign: "center", color: "#9D2235", fontWeight: 600 }}
        >
          Confirm Selection
        </DialogTitle>
        <DialogContent>
          <Typography align="center" sx={{ mb: 2 }}>
            You have selected{" "}
            <strong>
              {selectedEvalType === "ai" ? "AI-Based" : "Human-Based"}{" "}
              Evaluation
            </strong>
            .<br />
            Do you want to continue with this selection?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            onClick={() => setShowEvalConfirmation(false)}
            variant="outlined"
            sx={{
              borderColor: "#9D2235",
              color: "#9D2235",
              "&:hover": {
                borderColor: "#7a182a",
                bgcolor: "rgba(157, 34, 53, 0.05)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => setShowEvalConfirmation(false)}
            variant="contained"
            sx={{
              bgcolor: "#9D2235",
              "&:hover": { bgcolor: "#7a182a" },
              ml: 2,
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* COMMENTED: Paper-based modal - directly show computer-based */}
       {/* <Dialog
        open={showSubmissionTypeDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 2 } }}
      >
        <DialogTitle
          sx={{ textAlign: "center", color: "#9D2235", fontWeight: 600 }}
        >
          Choose Submission Method
        </DialogTitle>
        <DialogContent>
          <Typography align="center" sx={{ mb: 2 }}>
            How do you want to submit your writing for human evaluation?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            onClick={() => handleSubmissionTypeSelect("computer")}
            variant="contained"
            sx={{
              bgcolor: "#9D2235",
              "&:hover": { bgcolor: "#7a182a" },
              minWidth: 120,
            }}
          >
            Computer-Based
          </Button>
          <Button
            onClick={() => handleSubmissionTypeSelect("paper")}
            variant="contained"
            sx={{
              bgcolor: "#666",
              "&:hover": { bgcolor: "#333" },
              minWidth: 120,
              ml: 2,
            }}
          >
            Paper-Based
          </Button>
        </DialogActions>
      </Dialog>  */}

      {/* Submission Confirmation Dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 20px 40px rgba(157, 34, 53, 0.15)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#9D2235",
            color: "white",
            textAlign: "center",
            py: 3,
            fontSize: "1.5rem",
            fontWeight: 600,
            borderBottom: "none",
          }}
        >
          {location.state?.isPracticeMode &&
          location.state?.sectionOrder &&
          location.state?.currentSectionIndex ===
            location.state?.sectionOrder.length - 1
            ? "🏁 Finish Test"
            : "📤 Submit Answer"}
        </DialogTitle>
        <DialogContent
          sx={{
            backgroundColor: "white",
            py: 4,
            px: 4,
            textAlign: "center",
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="h6"
              sx={{
                color: "#9D2235",
                fontWeight: 600,
                mb: 2,
              }}
            >
              ⚠️ Confirmation Required
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#555",
                lineHeight: 1.6,
                fontSize: "1.1rem",
              }}
            >
              {location.state?.isPracticeMode &&
              location.state?.sectionOrder &&
              location.state?.currentSectionIndex ===
                location.state?.sectionOrder.length - 1
                ? "Are you sure you want to finish this test? You won't be able to make any changes after submission."
                : "Are you sure you want to submit your answer? You won't be able to make any changes after submission."}
            </Typography>
          </Box>
          <Box
            sx={{
              backgroundColor: "#f8f9fa",
              borderRadius: 2,
              p: 2,
              mt: 3,
              border: "1px solid #e9ecef",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "#666",
                fontStyle: "italic",
              }}
            >
              💡 Please review your work before proceeding
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            backgroundColor: "#f8f9fa",
            p: 3,
            gap: 2,
            justifyContent: "center",
          }}
        >
          <Button
            onClick={() => setConfirmOpen(false)}
            variant="outlined"
            sx={{
              borderColor: "#9D2235",
              color: "#9D2235",
              px: 4,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600,
              textTransform: "none",
              fontSize: "1rem",
              "&:hover": {
                borderColor: "#7a1a2a",
                backgroundColor: "rgba(157, 34, 53, 0.04)",
              },
            }}
          >
            ← Cancel
          </Button>
          <Button
            onClick={handleConfirmSubmit}
            variant="contained"
            sx={{
              backgroundColor: "#9D2235",
              color: "white",
              px: 4,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600,
              textTransform: "none",
              fontSize: "1rem",
              boxShadow: "0 4px 12px rgba(157, 34, 53, 0.3)",
              "&:hover": {
                backgroundColor: "#7a1a2a",
                boxShadow: "0 6px 16px rgba(157, 34, 53, 0.4)",
              },
            }}
          >
            {location.state?.isPracticeMode &&
            location.state?.sectionOrder &&
            location.state?.currentSectionIndex ===
              location.state?.sectionOrder.length - 1
              ? "✓ Finish Test"
              : "✓ Submit Answer"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Results Popup */}
      <TestResultsPopup
        open={showResultsPopup}
        onClose={() => setShowResultsPopup(false)}
        resultsData={testResults}
        onContinue={() =>
          navigate(location.state?.backRoute || "/app/mocktests")
        }
        attemptId={studentAttemptId}
        mockTestId={testId}
        baseRoute={location.state?.baseRoute || "/mock-test"}
        backRoute={location.state?.backRoute || "/app/mocktests"}
      />
    </>
  );
}
